// linkifyMessage — system announce 本文 (プレーンテキスト) 中の http(s) URL を
// クリック可能な <a> に変換する共有ヘルパ.
//
// 背景: `system.announce.posted` の message はプレーンテキスト (max512) で、
// AnnounceEntryModal / AnnounceBanner の双方が直描画している. Modern リリース
// 告知などで「詳しくはこちら https://...」の導線を出したいので、**web の描画側
// だけ** で URL をリンク化する (= protocol 変更なし).
//
// 安全性:
//   - message は信頼できない可能性のあるプレーンテキスト. **HTML として解釈
//     させない** (= dangerouslySetInnerHTML は使わない). 文字列を URL 正規表現
//     で分割し、テキスト断片は React のデフォルトエスケープに任せ、URL 部分のみ
//     <a> 要素にする.
//   - リンク化対象は **http:// / https:// のみ**. 正規表現が http(s) 以外に
//     マッチしないので `javascript:` 等の危険スキームは構造上 href に入らない
//     (= 生テキストとしてそのまま表示される).
//   - <a> は target="_blank" + rel="noopener noreferrer" で開く.

import type { ReactNode } from 'react';

// http:// または https:// で始まる URL を検出する正規表現.
//   - スキームは http/https のみ (= javascript: 等を構造的に排除).
//   - URL 本体は「空白・CJK 句読点・各種閉じ括弧・引用符・山括弧を含まない
//     1 文字以上」. これらは日本語文中で URL の直後に来やすい自然な境界なので、
//     character class の時点で除外する. こうしないと「example.com。続き」のように
//     句点 + 後続テキストまで貪欲に食う ([^\s]+ の取りこぼし).
//   - ASCII の `.,!?;:` 等はパスに正当に現れ得るので class には残し、**末尾** に
//     来た分だけ後段 splitTrailing で剥がす.
// 文字クラス内は意図せぬ range を作らない順序で列挙し、`]` はエスケープ.
const URL_RE = /https?:\/\/[^\s。、，．！？「」『』（）【】〔〕｛｝〈〉《》"'<>()[\]{}]+/g;

// URL 末尾に食われやすい ASCII 記号 (区切り・閉じ括弧・引用符). class には残して
// いるが、URL の **末尾** に連続する分はリンクから外す (= 「example.com/x.」の
// 「.」、「(https://example.com/y)」の「)」など). 内部の "/" 等は対象外、末尾の
// 連続のみ。CJK 句読点・全角括弧は URL_RE 時点で既に除外済み。
const TRAILING_CHARS = '.,!?;:)]}>\'"';

/**
 * URL マッチ末尾の区切り記号・閉じ括弧を剥がす. 返り値は `{ url, trailing }` で、
 * url はリンク化する本体、trailing はテキストへ戻す尾。
 */
const splitTrailing = (raw: string): { url: string; trailing: string } => {
  let end = raw.length;
  while (end > 0 && TRAILING_CHARS.includes(raw[end - 1] as string)) {
    end -= 1;
  }
  return { url: raw.slice(0, end), trailing: raw.slice(end) };
};

export interface LinkifyOptions {
  /** <a> に当てる className (= レイアウトごとの控えめなリンクスタイル). */
  readonly linkClassName?: string;
}

/**
 * プレーンテキストの message を React ノード配列に変換する純粋関数.
 *
 * - http(s) URL を `<a target="_blank" rel="noopener noreferrer">` にする.
 * - それ以外の断片は素の string として返す (= React がエスケープする).
 * - URL が 1 個も無ければ `[text]` (= 単一の string) を返し、従来の直描画と
 *   完全に同じ表示になる.
 *
 * key は URL 出現順インデックス (= 入力 message に対して安定).
 */
export const linkifyMessage = (text: string, options: LinkifyOptions = {}): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  // 正規表現の lastIndex は呼び出しごとにリセット (= module 共有 RegExp の罠回避).
  URL_RE.lastIndex = 0;
  let match: RegExpExecArray | null = URL_RE.exec(text);
  while (match !== null) {
    const matchStart = match.index;
    const raw = match[0];
    const { url, trailing } = splitTrailing(raw);

    // splitTrailing で URL がスキームのみ以下に縮んだ (= "http://" 直後が全部
    // 記号) 場合はリンク化せず生テキスト扱い. 通常は起きないが防御的に.
    if (url.length <= 'https://'.length) {
      nodes.push(text.slice(lastIndex, matchStart + raw.length));
      lastIndex = matchStart + raw.length;
      match = URL_RE.exec(text);
      continue;
    }

    // URL の前にあるプレーンテキスト断片.
    if (matchStart > lastIndex) {
      nodes.push(text.slice(lastIndex, matchStart));
    }

    nodes.push(
      <a
        key={`lnk-${key}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={options.linkClassName}
      >
        {url}
      </a>,
    );
    key += 1;

    // 末尾から剥がした記号はプレーンテキストとして戻す.
    if (trailing.length > 0) {
      nodes.push(trailing);
    }

    lastIndex = matchStart + raw.length;
    match = URL_RE.exec(text);
  }

  // 最後の URL より後ろの残りテキスト.
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  // URL が 1 個も無ければ元の text をそのまま返す (= 従来表示と同一).
  if (nodes.length === 0) {
    return [text];
  }

  return nodes;
};
